import _ from "lodash";
import React from "react";
import i18next from "src/i18n/config";
import { useAppSettingsService } from "src/services/appSettingsService/appSettingsService";
import { useDispatch, useSelector } from "src/store";
import { fetchProfile, selectProfile } from "src/store/account";
import { usePrefetch as usePrefetchEvents } from "src/store/api/events";
import { selectCurrentWorkspaceId, setCurrentWorkspaceId, setWorkspaceResolved } from "src/store/app";
import { selectHasValidSession } from "src/store/auth";

import { fetchGroups, fetchLicenses } from "@dashboard/devices/store";
import { Subscription } from "@dashboard/devices/types";
import { usePrefetch as usePrefetchReleases } from "@dashboard/downloads/api";
import { releaseFilesPageSize } from "@dashboard/downloads/hooks/useReleaseFiles";
import { usePrefetch as usePrefetchFiles } from "@dashboard/files/api";
import { normalizeFilesQueryParams } from "@dashboard/files/api/helpers";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getActivityReportScope } from "@dashboard/overview/components/EventChartCard/EventChartCard.utils";
import { usePrefetch as usePrefetchPolicies } from "@dashboard/policies/api";
import { productsApi, usePrefetch as usePrefetchProducts } from "@dashboard/products/api";
import { getDefaultProduct, sortProductsByName } from "@dashboard/products/utils/getDefaultProduct/getDefaultProduct";
import { fetchProvisionings } from "@dashboard/provisioning/store";
import { usePrefetch as usePrefetchFdroids } from "@dashboard/repositories/api";
import { useAllWorkspaces, useCurrentWorkspace, useSubscriptions } from "@dashboard/workspaces/api/helpers";
import { skipToken } from "@reduxjs/toolkit/query";
import UserSettingsService from "@services/userSettingsService";
import { showErrorToast } from "@shared/toasts/Toasts";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";

import "@services/timeService/timeService";

const licensePrefetchPageSize = 48;

export function getDefaultSubscription(subscriptions: Subscription[]) {
    const mostRecentActive = _.orderBy(
        subscriptions.filter((subscription) => subscription.status === "Active"),
        ["creationDate"],
        ["desc"],
    )[0];

    return mostRecentActive ?? subscriptions[0];
}

const usePrefetchWorkspacesQuery = () => {
    const hasValidSession = useSelector(selectHasValidSession);

    const { data: subscriptions } = useSubscriptions({}, { skip: !hasValidSession });
    const defaultSubscription = getDefaultSubscription(subscriptions);

    const allWorkspaces = useAllWorkspaces({}, { skip: !hasValidSession });
    const { data: workspaces, isLoading: isWorkspacesLoading, isFetching: isWorkspacesFetching } = allWorkspaces;
    // Every workspace-dependent view reads this same cache entry and none of them shows an error state, so this is
    // the one place a failed workspaces request is reported.
    useQueryErrorToast(allWorkspaces);
    const defaultWorkspace = workspaces?.find((workspace) => workspace.subscription.id === defaultSubscription?.id);

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const {
        data: currentWorkspace,
        isLoading: isCurrentWorkspaceLoading,
        isFetching: isCurrentWorkspaceFetching,
    } = useCurrentWorkspace();

    const isWorkspaceIdWrong = workspaceId && !currentWorkspace && !isCurrentWorkspaceFetching;
    const noCurrentWorkspace = !workspaceId && !isWorkspacesFetching;

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (noCurrentWorkspace && defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
        } else if (noCurrentWorkspace && !defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(null));
        } else if (isWorkspaceIdWrong && defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(defaultWorkspace.id));
        } else if (isWorkspaceIdWrong && !defaultWorkspace) {
            dispatch(setCurrentWorkspaceId(null));
        }
    }, [isWorkspaceIdWrong, noCurrentWorkspace, defaultWorkspace, workspaceId, dispatch]);

    // Workspace resolution is pending only on the FIRST load (isLoading, i.e. no workspace data
    // yet) — not during background refetches (isFetching). A background refetch keeps the existing
    // data, so gating on isFetching here would blank the whole dashboard for its duration. This is
    // exactly what happens when creating/deleting a group invalidates the Workspaces cache: the
    // refetch would otherwise unmount the page (PrefetchGuard renders null while resolving). The
    // last term covers the gap where a default workspace is ready but its ID hasn't been written
    // to the store yet (useEffect hasn't fired).
    const isResolvingWorkspace = isWorkspacesLoading || isCurrentWorkspaceLoading || (noCurrentWorkspace && !!defaultWorkspace);

    React.useEffect(() => {
        if (!isResolvingWorkspace) {
            dispatch(setWorkspaceResolved(true));
        }
    }, [isResolvingWorkspace, dispatch]);

    return { isResolvingWorkspace };
};

const useFetchProfile = () => {
    const [progress, setProgress] = React.useState(0);
    const [hasPrefetchError, setHasPrefetchError] = React.useState(false);

    const dispatch = useDispatch();
    const hasValidSession = useSelector(selectHasValidSession);

    React.useLayoutEffect(() => {
        if (!hasValidSession) {
            return;
        }

        void dispatch(fetchProfile())
            .unwrap()
            .catch(() => {
                setHasPrefetchError(true);
            })
            .finally(() => setProgress((prev) => prev + 100));

        return () => setProgress(0);
    }, [dispatch, hasValidSession]);

    return { progress, hasPrefetchError };
};

const usePrefetchFilesQuery = () => {
    const prefetchUser = usePrefetchFiles("getFiles");

    const hasValidSession = useSelector(selectHasValidSession);
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();

    React.useEffect(() => {
        if (hasValidSession && workspaceId && !isPageFlagsLoading && effectiveWorkspaceSettings.isFilesPageEnabled) {
            prefetchUser(normalizeFilesQueryParams(UserSettingsService.getFilePageParams(), workspaceId));
        }
    }, [hasValidSession, workspaceId, isPageFlagsLoading, effectiveWorkspaceSettings.isFilesPageEnabled, prefetchUser]);
};

// A failed workspace-data prefetch must not block the pages: the account pages hold the only workspace switcher,
// so replacing them with an error page would trap the user in a workspace that cannot be loaded.
const showWorkspaceDataError = () => showErrorToast(i18next.t("common:error.technicalDifficulties.message"));

const useFetchGroups = () => {
    const [progress, setProgress] = React.useState(0);

    const dispatch = useDispatch();
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    // The backend does not find the groups of a terminated workspace and answers with an error.
    const isTerminated = workspace?.subscription.status === "Terminated";

    const profile = useSelector(selectProfile);
    const isProfileCompleted = profile && profile.profileCompleted;

    React.useLayoutEffect(() => {
        if (isProfileCompleted && (!workspaceId || isTerminated)) {
            setProgress(100);
        }
        if (isProfileCompleted && workspaceId && !isTerminated) {
            void dispatch(fetchGroups(workspaceId))
                .unwrap()
                .then(() => {
                    setProgress((prev) => prev + 50);
                    return dispatch(
                        fetchLicenses({
                            groupId: workspace.subscription.groupIds,
                            isPrefetch: true,
                            pageSize: licensePrefetchPageSize,
                        }),
                    ).unwrap();
                })
                .then((responses) => {
                    setProgress((prev) => prev + 50);
                    return dispatch(
                        fetchLicenses({
                            groupId: workspace.subscription.groupIds,
                            prefetched: { pageSize: licensePrefetchPageSize, responses },
                        }),
                    ).unwrap();
                })
                .catch(() => {
                    showWorkspaceDataError();
                    setProgress(100);
                });
        }
        return () => setProgress(0);
        // Depending on `subscription.groupIds` would re run the whole prefetch, progress reset included, every
        // time a group is created or deleted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, workspaceId, isTerminated, isProfileCompleted]);

    return { progress };
};

const useFetchFdroids = () => {
    const prefetchFdroids = usePrefetchFdroids("getFdroids");
    const hasValidSession = useSelector(selectHasValidSession);
    const { data: workspace } = useCurrentWorkspace();
    const subscriptionId = workspace?.subscription.id;
    const isTerminated = workspace?.subscription.status === "Terminated";

    React.useLayoutEffect(() => {
        if (hasValidSession && subscriptionId) {
            prefetchFdroids({ subscriptionId, isTerminated });
        }
    }, [hasValidSession, subscriptionId, isTerminated, prefetchFdroids]);
};

const useFetchProducts = () => {
    const prefetchProducts = usePrefetchProducts("fetchProducts");
    const hasValidSession = useSelector(selectHasValidSession);
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription?.status === "Terminated";

    React.useLayoutEffect(() => {
        if (hasValidSession && workspaceId) {
            prefetchProducts({ workspaceId, isTerminated });
        }
    }, [hasValidSession, workspaceId, isTerminated, prefetchProducts]);
};

const useFetchDefaultProductDetails = () => {
    const prefetchById = usePrefetchProducts("fetchProductById");
    const prefetchBuilds = usePrefetchProducts("fetchProductBuilds");
    const prefetchVariables = usePrefetchProducts("fetchProductVariables");
    const prefetchChangeLogs = usePrefetchProducts("fetchProductChangeLogs");
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription?.status === "Terminated";

    const selectProducts = React.useMemo(
        () => productsApi.endpoints.fetchProducts.select(workspaceId ? { workspaceId, isTerminated } : skipToken),
        [workspaceId, isTerminated],
    );
    const products = useSelector(selectProducts).data;
    const defaultProductId = products ? getDefaultProduct(sortProductsByName(products))?.id : undefined;

    React.useLayoutEffect(() => {
        if (defaultProductId) {
            prefetchById(defaultProductId);
            prefetchBuilds(defaultProductId);
            prefetchVariables(defaultProductId);
            prefetchChangeLogs(defaultProductId);
        }
    }, [defaultProductId, prefetchById, prefetchBuilds, prefetchVariables, prefetchChangeLogs]);
};

const useFetchPolicies = () => {
    const prefetchPolicies = usePrefetchPolicies("getPolicies");
    const hasValidSession = useSelector(selectHasValidSession);
    // Read the id and the expiry from the same loaded workspace. The selected id in the store switches before the
    // new workspace has loaded; pairing it with the expiry flag of the previous (or no) workspace would request
    // policies for an expired workspace, which the backend refuses with an error toast.
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isSubscriptionExpired = Boolean(workspace?.subscription.isExpired);
    const { rawTenantSettings } = useAppSettingsService();
    const isPoliciesPageEnabled = rawTenantSettings.features.pages.policiesPageEnabled;

    React.useLayoutEffect(() => {
        if (hasValidSession && workspaceId && !isSubscriptionExpired && isPoliciesPageEnabled) {
            prefetchPolicies({ workspaceId });
        }
    }, [hasValidSession, workspaceId, isSubscriptionExpired, isPoliciesPageEnabled, prefetchPolicies]);
};

const useFetchActivityReport = () => {
    const prefetchActivityReport = usePrefetchEvents("getActivityReport");
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const groups = useSelector(selectGroups);
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();
    const isActivityChartEnabled = effectiveWorkspaceSettings.isOverviewPageShowingActivityChart;

    React.useEffect(() => {
        if (workspaceId && groups.length > 0 && !isPageFlagsLoading && isActivityChartEnabled) {
            const scope = getActivityReportScope(
                UserSettingsService.getOverviewPageDayRange(),
                UserSettingsService.getOverviewPageGroupFilter(),
                groups,
            );
            prefetchActivityReport({ workspaceId, ...scope });
        }
    }, [workspaceId, groups, isPageFlagsLoading, isActivityChartEnabled, prefetchActivityReport]);
};

const useFetchReleases = () => {
    const prefetchReleases = usePrefetchReleases("getReleases");
    const hasValidSession = useSelector(selectHasValidSession);
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription?.status === "Terminated";
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();
    const needsReleases = effectiveWorkspaceSettings.isDownloadsPageEnabled || effectiveWorkspaceSettings.isEnrollmentPageEnabled;

    React.useLayoutEffect(() => {
        if (hasValidSession && workspaceId && !isTerminated && !isPageFlagsLoading && needsReleases) {
            prefetchReleases({ workspaceId, pageSize: releaseFilesPageSize });
        }
    }, [hasValidSession, workspaceId, isTerminated, isPageFlagsLoading, needsReleases, prefetchReleases]);
};

const useFetchProvisionings = () => {
    const [progress, setProgress] = React.useState(0);

    const dispatch = useDispatch();
    const { data: workspace } = useCurrentWorkspace();
    const workspaceId = workspace?.id;
    const isTerminated = workspace?.subscription.status === "Terminated";
    const { effectiveWorkspaceSettings, isLoading: isPageFlagsLoading } = useAppSettingsService();

    const profile = useSelector(selectProfile);
    const isProfileCompleted = profile && profile.profileCompleted;

    React.useLayoutEffect(() => {
        if (isProfileCompleted && workspaceId && !isPageFlagsLoading && effectiveWorkspaceSettings.isProvisioningPageEnabled) {
            void dispatch(fetchProvisionings({ workspaceId, isTerminated }))
                .unwrap()
                .catch(showWorkspaceDataError)
                .finally(() => setProgress((prev) => prev + 100));
        } else {
            setProgress(100);
        }

        return () => setProgress(0);
    }, [
        dispatch,
        workspaceId,
        isTerminated,
        isPageFlagsLoading,
        effectiveWorkspaceSettings.isProvisioningPageEnabled,
        isProfileCompleted,
    ]);

    return { progress };
};

export const usePrefetch = () => {
    const { isResolvingWorkspace } = usePrefetchWorkspacesQuery();

    usePrefetchFilesQuery();
    useFetchFdroids();
    useFetchProducts();
    useFetchDefaultProductDetails();
    useFetchPolicies();
    useFetchReleases();
    useFetchActivityReport();

    const { progress: groupProgress } = useFetchGroups();
    const { progress: profileProgress, hasPrefetchError } = useFetchProfile();
    const { progress: provisioningsProgress } = useFetchProvisionings();

    const stages = [groupProgress, provisioningsProgress, profileProgress];

    return {
        progress: _.sum(stages) / stages.length,
        hasPrefetchError,
        isResolvingWorkspace,
    };
};
